import type { AdminOnboarding, AdminOnboardingNextStep } from '../api/admin.api'
import { formatAdminDateTime } from './kyc-support-format'
import { stripeFieldLabel } from './stripe-kyc-labels'

export const onboardingBadgeLabels: Readonly<Record<AdminOnboarding['badge'], string>> = {
  in_progress: 'Cadastro em andamento',
  in_review: 'Em análise',
  correction_needed: 'Correção necessária',
  approved: 'Cadastro aprovado',
}

export const onboardingStepLabels = {
  profile: {
    pending: 'Pendente',
    in_review: 'Em análise',
    fix: 'Corrigir',
    done: 'Concluído',
  },
  identity: {
    unavailable: 'Indisponível',
    preparing: 'Preparando',
    pending: 'Pendente',
    in_review: 'Em análise',
    fix: 'Corrigir',
    done: 'Concluído',
  },
  bank: {
    unavailable: 'Indisponível',
    pending: 'Pendente',
    fix: 'Corrigir',
    done: 'Concluído',
  },
} as const

export const operationLabels = {
  sales: {
    waiting: 'Aguardando conclusão do cadastro',
    in_review: 'Em análise',
    released: 'Liberadas',
    blocked: 'Bloqueadas',
  },
  payouts: {
    waiting: 'Aguardando conclusão do cadastro',
    in_review: 'Em análise',
    released: 'Liberados',
    blocked: 'Bloqueados',
  },
} as const

export const nextStepOwnerLabels: Readonly<Record<AdminOnboardingNextStep['owner'], string>> = {
  seller: 'Vendedor',
  stripe: 'Stripe',
  support: 'Suporte',
  none: 'Nenhum',
}

export function nextStepText(nextStep: AdminOnboardingNextStep): string {
  switch (nextStep.code) {
    case 'save_profile':
      return 'Salvar dados cadastrais'
    case 'fix_field': {
      const label = nextStep.requirementId
        ? stripeFieldLabel(nextStep.requirementId, 'form')
        : undefined
      return `Corrigir ${label ?? nextStep.requirementId ?? 'campo do cadastro'}`
    }
    case 'identity_document':
      return 'Enviar documento com foto e selfie'
    case 'add_bank_account':
      return 'Cadastrar conta bancária'
    case 'fix_bank_account':
      return 'Corrigir conta bancária'
    case 'wait_review':
      return nextStep.since
        ? `Em análise desde ${formatAdminDateTime(nextStep.since)}`
        : 'Em análise'
    case 'rejected':
      return 'Conta recusada pela Stripe'
    case 'activation_failed':
      return nextStep.owner === 'seller' ? 'Tentar ativar novamente' : 'Criação da conta falhou'
    case 'unsupported_requirement':
      return 'A Stripe pediu algo que o cadastro não coleta'
    case 'account_disabled':
      return 'Conta desativada pela Stripe'
    case 'sync_failing':
      return nextStep.since
        ? `Sincronização falhando desde ${formatAdminDateTime(nextStep.since)}`
        : 'Sincronização falhando'
    case 'none':
      return 'Nenhuma ação'
  }
}

export function nextStepHint(nextStep: AdminOnboardingNextStep): string | null {
  if (nextStep.code === 'activation_failed' && nextStep.owner === 'seller') {
    return 'Oriente o vendedor a clicar em Tentar novamente no painel.'
  }
  if (nextStep.owner === 'seller') return 'Oriente o vendedor a concluir no painel.'
  if (nextStep.owner === 'stripe') return 'Nada a fazer; escale se passar de 3 dias.'
  if (nextStep.owner === 'support') {
    return 'Escale para quem tem acesso ao Dashboard da Stripe (busque pelo e-mail do vendedor).'
  }
  return null
}

export function statusTone(
  value: string,
): 'success' | 'awaiting' | 'closed' | 'informative' | 'neutral' {
  if (value === 'done' || value === 'released' || value === 'approved') return 'success'
  if (value === 'in_review' || value === 'wait_review' || value === 'fix') return 'awaiting'
  if (value === 'blocked' || value === 'correction_needed') return 'closed'
  if (value === 'preparing' || value === 'in_progress') return 'informative'
  return 'neutral'
}
